import { useMemo } from 'react';
import styled from 'styled-components';
import { FlexColumn } from '@elementary/common-ui/components';
import { UseFormReturn } from 'react-hook-form';
import { AddTestForm, ColumnResource, Resource } from 'shared/types';
import { getSharedTimestampColumnNames } from 'testManagement/utils/wizardTimestampFields';
import ColumnsAcceptedValues from './WizardConfigurationStagePages/ColumnsAcceptedValues';
import ColumnsNotNull from './WizardConfigurationStagePages/ColumnsNotNull';
import ColumnsRelationship from './WizardConfigurationStagePages/ColumnsRelationship';
import ColumnsUnique from './WizardConfigurationStagePages/ColumnsUnique';
import CustomQuery from './WizardConfigurationStagePages/CustomQuery';
import TablesDimensionAnomalies from './WizardConfigurationStagePages/TablesDimensionAnomalies';
import TablesEventFreshnessAnomalies from './WizardConfigurationStagePages/TablesEventFreshnessAnomalies';
import TablesFreshnessAnomalies from './WizardConfigurationStagePages/TablesFreshnessAnomalies';
import TablesFreshnessAnomaliesCloud from './WizardConfigurationStagePages/TablesFreshnessAnomaliesCloud';
import TablesPipelineTaskPerformanceCloud from './WizardConfigurationStagePages/TablesPipelineTaskPerformanceCloud';
import TablesDataContract from './WizardConfigurationStagePages/TablesDataContract';
import TablesSchemaChangesAnomalies from './WizardConfigurationStagePages/TablesSchemaChangesAnomalies';
import TablesVolumeAnomalies from './WizardConfigurationStagePages/TablesVolumeAnomalies';
import TablesVolumeAnomaliesCloud from './WizardConfigurationStagePages/TablesVolumeAnomaliesCloud';
import YamlTest from './WizardConfigurationStagePages/YamlTest';

const YamlStage = styled(FlexColumn)`
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

type Props = {
  methods: UseFormReturn<AddTestForm, any, undefined>;
  columnsResourcesData?: ColumnResource[];
  singularTestPaths?: string[];
  tableResourcesData?: Resource[];
  editMode?: boolean;
  skipDefaults?: boolean;
  isRequiredDataAccessTest?: boolean;
};

const WizardConfigurationStage = ({
  methods,
  columnsResourcesData = [],
  singularTestPaths = [],
  tableResourcesData = [],
  editMode,
  skipDefaults,
  isRequiredDataAccessTest,
}: Props) => {
  const resourceType = methods.watch('resourceType');
  const testType = methods.watch('testType');
  const resources = methods.watch('resources');

  const timestampColumns = useMemo(
    () =>
      getSharedTimestampColumnNames(columnsResourcesData, resources ?? []).map(
        (name) => ({ label: name, value: name }),
      ),
    [columnsResourcesData, resources],
  );

  if (testType === 'customQuery') {
    return (
      <FlexColumn sx={{ p: 3, minWidth: 0, width: '100%' }}>
        <CustomQuery
          methods={methods}
          singularTestPaths={singularTestPaths}
          tableResourcesData={tableResourcesData}
          isRequiredDataAccessTest={isRequiredDataAccessTest}
        />
      </FlexColumn>
    );
  }

  if (testType === 'yamlTest') {
    return (
      <YamlStage>
        <YamlTest
          methods={methods}
          appliesTo={resourceType === 'tables' ? 'table' : 'column'}
          editMode={editMode}
          columnsResourcesData={columnsResourcesData}
          tableResourcesData={tableResourcesData}
        />
      </YamlStage>
    );
  }

  if (resourceType === 'tables') {
    if (testType === 'freshnessAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesFreshnessAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'cloudFreshnessAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesFreshnessAnomaliesCloud
            methods={methods}
            skipDefaults={skipDefaults}
          />
        </FlexColumn>
      );
    }
    if (testType === 'eventFreshnessAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesEventFreshnessAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'volumeAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesVolumeAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'cloudVolumeAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesVolumeAnomaliesCloud
            methods={methods}
            skipDefaults={skipDefaults}
          />
        </FlexColumn>
      );
    }
    if (testType === 'dimensionAnomalies') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesDimensionAnomalies
            methods={methods}
            timestampColumns={timestampColumns}
          />
        </FlexColumn>
      );
    }
    if (testType === 'schemaChanges') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesSchemaChangesAnomalies methods={methods} />
        </FlexColumn>
      );
    }
    if (testType === 'cloudDataContract') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesDataContract methods={methods} />
        </FlexColumn>
      );
    }
    if (testType === 'cloudPipelineTaskPerformance') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <TablesPipelineTaskPerformanceCloud
            methods={methods}
            skipDefaults={skipDefaults}
          />
        </FlexColumn>
      );
    }
  }

  if (resourceType === 'columns') {
    if (testType === 'unique') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsUnique
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
    if (testType === 'notNull') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsNotNull
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
    if (testType === 'acceptedValues') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsAcceptedValues
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
    if (testType === 'relationship') {
      return (
        <FlexColumn sx={{ p: 3 }}>
          <ColumnsRelationship
            methods={methods}
            columnsResourcesData={columnsResourcesData}
          />
        </FlexColumn>
      );
    }
  }

  return <div />;
};

export default WizardConfigurationStage;
